/**
 * Copyright Mántum All Rights Reserved.
 */
import get from '../network/get';
import hash from '../hash';
import { isOnline, reconnect } from '../online';

import type { Subscriber, Unsubscriber, Readable, Parameter } from '../type';

export default function observable<T>(url: string): Readable<T> {
    const seed = hash();

    const controller = new AbortController();
    const signal = controller.signal;

    const events: Record<string, () => void> = {};
    const subscriptions: Record<string, Subscriber<T>> = {};

    const clear = () => {
        if (seed in events) {
            delete events[seed];
        }
    };

    const unsubscriber = (key: string) => {
        controller.abort();
        if (key in subscriptions) {
            delete subscriptions[key];
        }
    };

    const notify = (value: T | null): void => {
        for (const [, subscription] of Object.entries(subscriptions)) {
            subscription(value);
        }
    };

    const subscribe = (run: Subscriber<T>): Unsubscriber => {
        const key = hash();
        subscriptions[key] = run;

        return () => {
            return unsubscriber(key);
        };
    };

    const call = (parameter?: Parameter) => {
        const subscribed = Object.keys(subscriptions).length;
        if (subscribed === 0) {
            return;
        }

        const callback = () => {
            const request = get<T>(url, {
                parameter,
                signal
            });

            request.then((response) => {
                notify(response);
                clear();
            });

            request.catch((response) => {
                notify(response);
                clear();
            });
        };

        if (!isOnline()) {
            const event = reconnect(callback);
            if (!(seed in events)) {
                events[seed] = event;
            }
            return;
        }

        callback();
    };

    return {
        subscribe,
        call
    };
}
